import { useState } from "react";
import { Controller, useFormContext, type FieldPath } from "react-hook-form";

import { Box } from "ui/box";
import { Select } from "ui/select";
import { ColorField } from "containers/admin/clients/client/theme-configuration-page/components/color-field/color-field";

import { validateHexColor } from "containers/admin/clients/client/theme-configuration-page/brand-identity/colors-tab/colors-tab.helpers";
import { findColorOption } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.helpers";

import type {
  EmailConfigurationFormValues,
  ThemeColorOption,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.types";

import {
  ColorSwatch,
  FIELD_GROUP_SX,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.styles";
import {
  COLOR_CUSTOM_VALUE,
  COLOR_NONE_VALUE,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.constants";

import Icons from "assets";

type ThemeColorSelectProps = {
  name: FieldPath<EmailConfigurationFormValues>;
  label: string;
  options: ThemeColorOption[];
  /** Offers "None", which the legacy theme saves as `none` and paints transparent. */
  hasNone?: boolean;
  disabled: boolean;
};

const NONE_OPTION = { label: "None", value: COLOR_NONE_VALUE, icon: <Icons.ColorEmpty aria-hidden /> };

const CUSTOM_OPTION = { label: "Custom...", value: COLOR_CUSTOM_VALUE, icon: <Icons.ChooseColor aria-hidden /> };

/**
 * A palette colour or a custom hex, saved as the hex either way. A hex the palette holds is shown as that
 * palette colour; a custom one stays custom while it is being typed even when it happens to match one.
 */
export const ThemeColorSelect = ({ name, label, options, hasNone = false, disabled }: ThemeColorSelectProps) => {
  const { control } = useFormContext<EmailConfigurationFormValues>();
  const [isCustomChosen, setIsCustomChosen] = useState(false);

  const selectOptions = [
    ...options.map(({ label: optionLabel, value, color }) => ({
      label: optionLabel,
      value,
      icon: <ColorSwatch swatchColor={color} />,
    })),
    ...(hasNone ? [NONE_OPTION] : []),
    CUSTOM_OPTION,
  ];

  const isCustomColor = (color: string) =>
    isCustomChosen || (!!color && color !== COLOR_NONE_VALUE && !findColorOption(options, color));

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (value) => !isCustomColor(String(value ?? "")) || validateHexColor(label, true)(String(value ?? "")),
      }}
      render={({ field, fieldState }) => {
        const color = String(field.value ?? "");
        const isCustom = isCustomColor(color);
        const selectedValue = isCustom ? COLOR_CUSTOM_VALUE : (findColorOption(options, color)?.value ?? color);

        const handleSelectChange = (option: { value: string | number | boolean } | null) => {
          if (!option) {
            return;
          }

          setIsCustomChosen(option.value === COLOR_CUSTOM_VALUE);

          if (option.value === COLOR_CUSTOM_VALUE) {
            // A custom colour starts from the one shown, so switching to Custom changes nothing yet.
            field.onChange(color === COLOR_NONE_VALUE ? "" : color);
          } else if (option.value === COLOR_NONE_VALUE) {
            field.onChange(COLOR_NONE_VALUE);
          } else {
            field.onChange(options.find(({ value }) => value === option.value)?.color ?? "");
          }
        };

        // Editing the hex keeps the field custom, even once cleared or matching a palette colour.
        const handleHexChange = (hex: string) => {
          setIsCustomChosen(true);
          field.onChange(hex);
        };

        return (
          <Box sx={FIELD_GROUP_SX}>
            <Select
              selectName={`${name}-select`}
              ariaLabel={label}
              label={label}
              options={selectOptions}
              value={selectOptions.find(({ value }) => value === selectedValue) ?? null}
              onChange={handleSelectChange}
              disabled={disabled}
              disableClearable
              isSearchable={false}
            />
            {isCustom && (
              <ColorField
                label={`${label} hex`}
                name={field.name}
                value={color}
                onChange={handleHexChange}
                onBlur={field.onBlur}
                ref={field.ref}
                disabled={disabled}
                errMessage={fieldState.error?.message}
              />
            )}
          </Box>
        );
      }}
    />
  );
};
